import type React from 'react';
import { useEffect } from 'react';
import { Box, Page } from '@zillow/constellation-10';
import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { CustomizedBlockedUserModal } from '@zg-rentals/satellite-components';
import type { SystemStyleObject } from '@/styled-system/types';
import StringConstants from '../../constants/StringConstants';
import Link from 'next/link';
import { AppLayoutContainer } from '@zg-rentals/zrm-header-navigation';
import { useSatelliteWebLandlordConversationHandler_getCachedConversationCounts } from '@zg-rentals/ts-satellite-client-react-query';
import useBooleanConfig from '../../hooks/useBooleanConfig';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../../src/types/ModalType';
import { useAuth } from '../../AuthProvider';
import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';

export default function Layout({
  children,
  showFooter = true,
  showHeader = true,
  mainContentStyles = {},
  pageRootStyles = {},
  appLayoutContainerStyles = {},
  isPageFluid = false,
}: {
  children: React.ReactNode;
  showFooter?: boolean;
  showHeader?: boolean;
  mainContentStyles?: SystemStyleObject;
  pageRootStyles?: SystemStyleObject;
  appLayoutContainerStyles?: SystemStyleObject;
  isPageFluid?: boolean;
}) {
  const canAccessBlockedUserModalFromRJSModule = useBooleanConfig('canAccessBlockedUserModalFromRJSModule', false);
  const { activeModal, setActiveModal } = useModal();

  const hostName = typeof window !== 'undefined' && window !== null ? window.location.host : 'www.zillow.com';
  const pathname = typeof window !== 'undefined' && window !== null ? window.location.href : '';

  const { user } = useAuth();
  const { isEnterpriseCustomer, isUserBlocked, restrictions } = user ?? {};
  const { isAllowed } = restrictions ?? {};

  // Feed the top-nav unread messages badge from SLW's own satellite count so it
  // shares a single source with the inbox (same query key → deduped) instead of
  // the top nav issuing a second, RMA-backed count fetch.
  const { data: conversationCounts } = useSatelliteWebLandlordConversationHandler_getCachedConversationCounts({
    enabled: user != null,
    meta: {
      tags: ['satellite:conversationCounts'],
      context: { api: 'getCachedConversationCounts', details: 'Layout - top-nav unread messages badge count' },
    },
  });

  // The legacy `isUserBlocked` flag misses accounts restricted via `restrictions` alone (closed
  // accounts, for one). Those users clear the auth boundary, so this is the only gate that stops them.
  const isBlocked = isUserBlocked || isAllowed === false;
  // The module modal draws its title and CTA straight from `restrictions`, so it can only speak for
  // accounts the restriction system actually blocked. Everyone else falls back to the static copy.
  const shouldDisplayBlockedUserModalFromModule = canAccessBlockedUserModalFromRJSModule && isAllowed === false;
  const shouldDisplayCustomizedBlockedUserModal = isBlocked && !shouldDisplayBlockedUserModalFromModule;

  useEffect(() => {
    if (shouldDisplayCustomizedBlockedUserModal) {
      setActiveModal(ModalType.CustomizedBlockedModal);
    } else if (shouldDisplayBlockedUserModalFromModule) {
      setActiveModal(ModalType.BlockedModalFromModule);
    }
  }, [shouldDisplayCustomizedBlockedUserModal, shouldDisplayBlockedUserModalFromModule, activeModal]);

  return (
    <Page.Root
      fluid={isPageFluid}
      css={{ paddingBottom: '0', ...pageRootStyles, backgroundColor: 'bg.screen.neutral' }}
    >
      {!user?.streetEasyUserId && <AppDownloadUpsellBanner />}

      {/* use the blocked user modal from the blocked-user-modal module, default to use our version if the feature flag value is false */}
      {activeModal === ModalType.BlockedModalFromModule && user != null && <BlockedUserModal user={user} />}
      {activeModal === ModalType.CustomizedBlockedModal && <CustomizedBlockedUserModal brand="zillow" />}

      {showHeader && user != null && (
        <Box asChild css={appLayoutContainerStyles}>
          <AppLayoutContainer
            altLogo={!isEnterpriseCustomer}
            brand={StringConstants.UNFORMATTED_BRAND_NAMES.zillow}
            userModel={user}
            useRelativePaths={false}
            reactRouterLink={Link}
            host={hostName}
            pathname={pathname}
            displayMainMyProperties={true}
            hideFooter={!showFooter}
            unreadMessageCount={conversationCounts?.unreadCount ?? 0}
          >
            <Box asChild css={mainContentStyles}>
              <main>{children}</main>
            </Box>
          </AppLayoutContainer>
        </Box>
      )}
    </Page.Root>
  );
}
